import * as React from "react";
import { FunctionComponent } from "react";
import { InquiryCancelModalStyle } from "./inquiryCancelModalStyle";
import { Modal, Form } from "antd";
import { useFormatMessage } from "@utils/intlHook";
import TextArea from "antd/lib/input/TextArea";
import { ErrorMessage } from "@components/errorMessage/errorMessage";
import { useCancelInquiry } from "@graphql/hocs/hooks/useCancelInquiry";
import { FormComponentProps } from "antd/lib/form";
import { GraphQLDocuments } from "@graphql/graphQLDocuments";
import { InquiryOverviewFragment, InquiryStatusType } from "@models/graphql";
import { NormalizedCache } from "apollo-cache-inmemory";

interface FormValues {
    reason: string;
}
export interface InquiryCancelModalProps extends FormComponentProps<FormValues> {
    visible: boolean;
    inquiryId: string;
    assetQrVisible: boolean;
    closeCallback(): void;
}

export const InquiryCancelModalComponent: FunctionComponent<InquiryCancelModalProps> = ({ visible, closeCallback, inquiryId, assetQrVisible, form }) => {
    const formatMessage = useFormatMessage();

    const { cancelInquiry, isLoading: cancelLoading, error: cancelError } = useCancelInquiry({
        update(cache, response) {
            if (!response || !response.data || !response.data.cancelInquiry) {
                return;
            }
            const data: NormalizedCache = (cache as any).data;

            Object.keys((data as any).data).forEach(key => key.match(/^CollectionRound/) && data.delete(key));
            Object.keys((data as any).data).forEach(key => key.match(/^\$ROOT_QUERY\.collectionRounds/) && data.delete(key));

            const inqFragment = cache.readFragment<InquiryOverviewFragment>({
                fragment: GraphQLDocuments.inquiryOverview,
                fragmentName: "inquiryOverview",
                id: `Inquiry:${inquiryId}`
            });

            if (inqFragment) {
                inqFragment.currentStatus = InquiryStatusType.Canceled;
                inqFragment.lastUpdated = new Date().toISOString();

                cache.writeFragment<InquiryOverviewFragment>({
                    fragment: GraphQLDocuments.inquiryOverview,
                    fragmentName: "inquiryOverview",
                    id: `Inquiry:${inquiryId}`,
                    data: inqFragment
                });
            }
        },
        refetchQueries: [{ query: GraphQLDocuments.getInquiryDetail, variables: {
            id: inquiryId,
            includeAssetOnStatusHistory: assetQrVisible
        } }],
        awaitRefetchQueries: true
    });

    const handleOK = (): void => {
        form.validateFields(async (errors, values) => {
            if (!errors) {
                const { response } = await cancelInquiry({
                    inquiryId,
                    reason: values.reason
                });

                if (response && response.data && response.data.cancelInquiry) {
                    closeCallback();
                }
            }
        });
    };

    return (
        <InquiryCancelModalStyle>
            <Modal
                title={formatMessage({ id: "inquiryDetail.cancelModalTitle" })}
                visible={visible}
                onOk={handleOK}
                confirmLoading={cancelLoading}
                onCancel={closeCallback}
                okText={formatMessage({ id: "save" })}
                cancelText={formatMessage({ id: "cancel" })}
            >
                <Form>
                    <Form.Item label={formatMessage({ id: "inquiryDetail.reason" })}>
                        {form.getFieldDecorator("reason", {
                            rules: [
                                { required: true, message: formatMessage({ id: "requiredField" }) }
                            ]
                        })(<TextArea rows={4} />)}
                    </Form.Item>
                    <ErrorMessage error={cancelError} />
                </Form>
            </Modal>
        </InquiryCancelModalStyle>
    );
};

export const InquiryCancelModal = Form.create<InquiryCancelModalProps>()(InquiryCancelModalComponent);
